import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { apiErrorMessage } from "@/lib/api";
import { parseUsdToMicros } from "@/lib/money";
import { recordManualConversion } from "@/api/cpa";

/**
 * "Record FTD" from the lead drawer — for deposits the advertiser reported
 * out-of-band (a Slack message, an email) instead of postback/CSV. One FTD
 * per lead: the backend answers 409 when one is already recorded, shown
 * inline here.
 */
export function RecordFtdDialog({
    leadId,
    open,
    onOpenChange,
}: {
    leadId: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}): React.ReactNode {
    const queryClient = useQueryClient();
    const [amountUsd, setAmountUsd] = React.useState("");
    const [occurredAt, setOccurredAt] = React.useState("");
    const [note, setNote] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (open) {
            setAmountUsd("");
            setOccurredAt("");
            setNote("");
            setError(null);
        }
    }, [open]);

    const recordMut = useMutation({
        mutationFn: (req: { leadId: number; amountUsdMicros: number; occurredAt?: string; note?: string }) =>
            recordManualConversion(req),
        onSuccess: () => {
            toast.success("Deposit recorded — it'll be priced by the matching deals");
            queryClient.invalidateQueries({ queryKey: ["conversions"] });
            queryClient.invalidateQueries({ queryKey: ["finance"] });
            onOpenChange(false);
        },
        onError: (err) => setError(apiErrorMessage(err, "Couldn't record the deposit")),
    });

    const submit = (e: React.FormEvent): void => {
        e.preventDefault();
        setError(null);
        const micros = parseUsdToMicros(amountUsd);
        if (micros == null) {
            setError("Enter a valid dollar amount for the deposit.");
            return;
        }
        recordMut.mutate({
            leadId,
            amountUsdMicros: micros,
            occurredAt: occurredAt ? new Date(occurredAt).toISOString() : undefined,
            note: note.trim() || undefined,
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="record-ftd-dialog">
                <DialogHeader>
                    <DialogTitle>Record a deposit (FTD) for lead #{leadId}</DialogTitle>
                    <DialogDescription>
                        Use this when the advertiser reported the deposit outside their postback or CSV feed. It counts
                        toward deals exactly like a reported conversion.
                    </DialogDescription>
                </DialogHeader>
                <form className="space-y-4" onSubmit={submit}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="ftd-amount">Deposit amount ($)</Label>
                            <Input
                                id="ftd-amount"
                                inputMode="decimal"
                                placeholder="e.g. 100.50"
                                value={amountUsd}
                                onChange={(e) => setAmountUsd(e.target.value)}
                                autoFocus
                                data-testid="record-ftd-amount"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="ftd-date">When (optional)</Label>
                            <Input
                                id="ftd-date"
                                type="datetime-local"
                                value={occurredAt}
                                onChange={(e) => setOccurredAt(e.target.value)}
                                data-testid="record-ftd-date"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="ftd-note">Note</Label>
                        <Textarea
                            id="ftd-note"
                            rows={2}
                            placeholder="e.g. reported by e-mail on Jul 3"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            data-testid="record-ftd-note"
                        />
                    </div>
                    {error && (
                        <p className="text-sm text-destructive" data-testid="record-ftd-error">
                            {error}
                        </p>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={recordMut.isPending}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={recordMut.isPending} data-testid="record-ftd-submit">
                            {recordMut.isPending ? <Spinner size="sm" /> : "Record deposit"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
